import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) {
      setStatus("error");
      return;
    }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
      } catch (e) {}
      if (!active) return;
      if (tries.current++ < 10) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md border border-[var(--line)] bg-white p-10 text-center" data-testid="payment-success">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[var(--accent)] animate-spin" />
            <h2 className="font-serif text-2xl mt-4">Confirming your subscription…</h2>
            <p className="text-sm text-[var(--text-2)] mt-2">Hold tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-[var(--business)]" data-testid="payment-paid" />
            <h2 className="font-serif text-3xl mt-4">Premium unlocked!</h2>
            <p className="text-sm text-[var(--text-2)] mt-2">Automated voice reminders are now enabled on your account.</p>
          </>
        )}
        {status === "pending" && (
          <>
            <AlertCircle className="w-10 h-10 mx-auto text-[var(--warning)]" />
            <h2 className="font-serif text-2xl mt-4">Payment processing</h2>
            <p className="text-sm text-[var(--text-2)] mt-2">It's taking longer than usual. Your access will update shortly.</p>
          </>
        )}
        {status === "error" && (
          <>
            <AlertCircle className="w-10 h-10 mx-auto text-[var(--danger)]" />
            <h2 className="font-serif text-2xl mt-4">Missing session</h2>
          </>
        )}
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/")}
          className="group mt-6 w-full h-11 bg-[#1A1A18] text-white flex items-center justify-center gap-2 hover:bg-[var(--accent)] transition-colors"
        >
          Back to dashboard <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
}
